import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, X, User, Shield, Bell, Image, Check, CheckCheck, Clock, AlertCircle, Paperclip, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();
const WALLET_ADDRESS = "0x2791a74dC88c61Ed50B2948C60aABeBBa8DF7b2e";

// Notification sound function
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
    
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.value = 1000;
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 0.2);
    }, 150);
  } catch (e) {
    console.log('Could not play notification sound');
  }
};

// Star Rating Component for Chat
const ChatStarRating = ({ rating, setRating }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1 justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={`w-8 h-8 ${
              (hover || rating) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-500'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review Form Component displayed in chat
const ReviewInChatForm = ({ reviewRequestId, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (reviewText.trim().length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('guestToken');
      const response = await fetch(`${API}/user/submit-review?token=${token}&request_id=${reviewRequestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          review_text: reviewText.trim(),
          display_name: displayName.trim() || null
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Thank you for your feedback!');
        setSubmitted(true);
        onSubmit && onSubmit();
      } else {
        toast.error(data.message || 'Failed to submit review');
      }
    } catch (err) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 text-center">
        <Star className="w-8 h-8 text-yellow-400 fill-yellow-400 mx-auto mb-2" />
        <p className="text-green-300 font-medium">Thank you for your feedback!</p>
        <p className="text-gray-400 text-sm">Your feedback will help us grow.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-4 space-y-3">
      <div className="text-center">
        <p className="text-sm text-gray-300 mb-2">How would you rate your experience?</p>
        <ChatStarRating rating={rating} setRating={setRating} />
        <p className="text-xs text-gray-500 mt-1">
          {rating === 0 ? 'Tap to rate' :
           rating === 1 ? 'Poor' :
           rating === 2 ? 'Fair' :
           rating === 3 ? 'Good' :
           rating === 4 ? 'Very Good' : 'Excellent!'}
        </p>
      </div>

      {rating >= 4 && (
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Your name (will be shown publicly)</label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your name"
            className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 text-sm"
            maxLength={50}
          />
        </div>
      )}

      <div>
        <label className="text-xs text-gray-400 mb-1 block">Your review</label>
        <Textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="Share your experience with our signals..."
          className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 text-sm min-h-[80px]"
          maxLength={500}
        />
        <p className="text-[10px] text-gray-500 text-right">{reviewText.length}/500</p>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || rating === 0}
        className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-medium"
      >
        {submitting ? 'Submitting...' : 'Submit Review'}
      </Button>
    </div>
  );
};

// User Messenger Modal (for users to chat with admin)
export const UserMessengerModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [showBotOptions, setShowBotOptions] = useState(true);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportName, setSupportName] = useState('');
  const [supportIssue, setSupportIssue] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [chatEnded, setChatEnded] = useState(false);
  const [lastAdminResponseTime, setLastAdminResponseTime] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-close after 20 minutes of no admin response
  const startAutoCloseTimer = () => {
    // Clear existing timer
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
    
    // Set new timer for 20 minutes (1200000ms)
    autoCloseTimerRef.current = setTimeout(() => {
      if (isOpen) {
        toast.info(t('noResponseTimeout') || 'Chat closed due to inactivity', { duration: 5000 });
        onClose();
      }
    }, 20 * 60 * 1000); // 20 minutes
  };

  // Reset timer when admin responds
  const resetAutoCloseTimer = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, []);

  // Bot greeting message
  const getBotGreeting = () => ({
    id: 'bot-greeting',
    sender_type: 'bot',
    message: t('botGreeting'),
    timestamp: new Date().toISOString()
  });

  const getBotQuestion = () => ({
    id: 'bot-question',
    sender_type: 'bot',
    message: t('botHowCanIHelp'),
    timestamp: new Date().toISOString()
  });

  // Payment info response
  const getPaymentBotResponse = () => ({
    id: 'bot-payment',
    sender_type: 'bot',
    message: `${t('paymentBotTitle')}\n\n${t('paymentBotIntro')}\n\n${t('paymentBotStep1')}\n${t('paymentBotStep2')}\n${t('paymentBotStep3')}\n${t('paymentBotStep4')}\n${t('paymentBotStep5')}\n\n${t('paymentBotWalletLabel')}\n${WALLET_ADDRESS}\n\n${t('paymentBotWarning')}`,
    timestamp: new Date().toISOString()
  });

  // Referral info response
  const getReferralBotResponse = () => ({
    id: 'bot-referral',
    sender_type: 'bot',
    message: `${t('referralBotTitle')}\n\n${t('referralBotIntro')}\n\n${t('referralBotStep1')}\n${t('referralBotStep2')}\n${t('referralBotStep3')}\n${t('referralBotStep4')}\n\n${t('referralBotNote')}`,
    timestamp: new Date().toISOString()
  });

  const fetchMessages = async (playSound = false) => {
    const token = localStorage.getItem('guestToken');
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/user/messages?token=${token}`);
      const data = await response.json();
      if (data.success) {
        const newMessages = data.messages || [];
        
        // Check for new admin messages
        if (playSound && newMessages.length > lastMessageCount) {
          const newAdminMessages = newMessages.filter(
            (m, i) => i >= lastMessageCount && m.sender_type === 'admin'
          );
          if (newAdminMessages.length > 0) {
            playNotificationSound();
            // Admin responded - reset the auto-close timer
            resetAutoCloseTimer();
            setLastAdminResponseTime(new Date());
          }
        }
        
        // Check if chat was ended - if so, show bot options for new conversation
        const endedMsg = newMessages.find(m => m.chat_ended);
        if (endedMsg) {
          setChatEnded(true);
          // Check if this is the last message (user returning after chat ended)
          const lastMsg = newMessages[newMessages.length - 1];
          if (lastMsg && lastMsg.chat_ended) {
            // Show bot options again for new conversation
            setShowBotOptions(true);
          }
        } else {
          setChatEnded(false);
        }
        
        // Track last admin response time
        const lastAdminMsg = [...newMessages].reverse().find(m => m.sender_type === 'admin');
        if (lastAdminMsg) {
          setLastAdminResponseTime(new Date(lastAdminMsg.timestamp));
        }
        
        setMessages(newMessages);
        setLastMessageCount(newMessages.length);
        
        // Hide bot options if there are real messages (non-system)
        const realMessages = newMessages.filter(m => !m.chat_ended && m.sender_type !== 'system');
        if (realMessages.length > 0 && !endedMsg) {
          setShowBotOptions(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('imageTooLarge'));
        return;
      }
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        toast.error(t('invalidImageType'));
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendMessage = async (messageText = null, isSupport = false) => {
    const text = messageText || newMessage.trim();
    if (!text && !selectedImage) return;

    const token = localStorage.getItem('guestToken');
    if (!token) return;

    setSending(true);
    try {
      let imageData = null;
      if (selectedImage) {
        imageData = imagePreview;
      }

      const response = await fetch(`${API}/user/messages/send?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: text,
          image: imageData,
          is_support_request: isSupport
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        setSelectedImage(null);
        setImagePreview(null);
        setShowBotOptions(false);
        // Start 20-minute auto-close timer after user sends message
        startAutoCloseTimer();
        fetchMessages();
      } else {
        toast.error(data.message || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleBotOption = (option) => {
    if (option === 'payment') {
      setMessages(prev => [...prev, getPaymentBotResponse()]);
      setShowBotOptions(false);
    } else if (option === 'referral') {
      setMessages(prev => [...prev, getReferralBotResponse()]);
      setShowBotOptions(false);
    } else if (option === 'support') {
      setShowSupportForm(true);
      setShowBotOptions(false);
    }
  };

  const handleSupportSubmit = async () => {
    if (!supportName.trim() || !supportIssue.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    const supportMessage = `[${t('talkToSupportTitle')}]\n\n📛 ${t('enterYourNameChat')}: ${supportName}\n\n📝 ${t('describeIssue')}:\n${supportIssue}`;
    
    await sendMessage(supportMessage, true);
    setShowSupportForm(false);
    setSupportName('');
    setSupportIssue('');
    toast.success(t('supportRequestSent'));
  };

  // User ends chat
  const endChat = async () => {
    const token = localStorage.getItem('guestToken');
    if (!token) return;

    try {
      const response = await fetch(`${API}/user/messages/end-chat?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setChatEnded(true);
        setShowBotOptions(true);
        toast.success(t('chatEnded'));
        fetchMessages();
      }
    } catch (err) {
      toast.error('Failed to end chat');
    }
  };

  // Start new conversation after chat ended
  const startNewConversation = async () => {
    const token = localStorage.getItem('guestToken');
    if (!token) return;

    try {
      const response = await fetch(`${API}/user/messages/start-new?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setChatEnded(false);
        setShowBotOptions(true);
        fetchMessages();
      }
    } catch (err) {
      console.error('Failed to start new conversation');
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMessages();
      const interval = setInterval(() => fetchMessages(true), 5000);
      return () => {
        clearInterval(interval);
        // Clear auto-close timer when modal closes
        resetAutoCloseTimer();
      };
    }
  }, [isOpen]); // eslint-disable-line

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!isOpen) return null;

  // Support Form Modal
  if (showSupportForm) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-md overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-purple-600/20 to-pink-600/20">
            <h3 className="text-white font-medium">{t('talkToSupportTitle')}</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowSupportForm(false)} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">{t('enterYourNameChat')}</label>
              <Input
                value={supportName}
                onChange={(e) => setSupportName(e.target.value)}
                placeholder={t('enterYourNameChat')}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">{t('describeIssue')}</label>
              <textarea
                value={supportIssue}
                onChange={(e) => setSupportIssue(e.target.value)}
                placeholder={t('describeIssue')}
                rows={4}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-gray-500 resize-none"
              />
            </div>
            <Button
              onClick={handleSupportSubmit}
              disabled={!supportName.trim() || !supportIssue.trim()}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              <Send className="w-4 h-4 mr-2" />
              {t('submitToSupport')}
            </Button>
            <p className="text-xs text-gray-400 text-center">{t('supportWillRespond')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-md h-[85vh] max-h-[600px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-medium">{t('supportChat')}</h3>
              <p className="text-xs text-gray-400">{t('chatWithAdmin')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* End Chat button for user */}
            {messages.length > 0 && !chatEnded && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={endChat}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
              >
                <Clock className="w-3 h-3 mr-1" />
                {t('endChat')}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 flex flex-col">
          {/* Chat Ended Notice with Start New Conversation button */}
          {chatEnded && !showBotOptions && (
            <div className="text-center py-4 space-y-3">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-red-300 text-sm">{t('chatEnded')}</span>
              </div>
              <div>
                <Button
                  onClick={startNewConversation}
                  variant="outline"
                  size="sm"
                  className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('startNewConversation') || 'Start New Conversation'}
                </Button>
              </div>
            </div>
          )}

          {loading && messages.length === 0 && !showBotOptions ? (
            <div className="text-center text-gray-500 py-8">{t('loadingMessages')}</div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                    msg.sender_type === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : msg.sender_type === 'bot'
                        ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white rounded-bl-sm border border-blue-500/30'
                        : msg.sender_type === 'system'
                          ? 'bg-gray-500/20 text-gray-300 rounded-bl-sm border border-gray-500/30 text-center mx-auto'
                          : msg.is_review_request
                            ? 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 text-white rounded-bl-sm'
                            : 'bg-white/10 text-white rounded-bl-sm'
                  }`}
                >
                  {/* Image if present */}
                  {msg.image && (
                    <img 
                      src={msg.image} 
                      alt="Shared" 
                      className="max-w-full rounded-lg mb-2 cursor-pointer"
                      onClick={() => window.open(msg.image, '_blank')}
                    />
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  
                  {/* Review Request Form */}
                  {msg.is_review_request && msg.review_request_id && (
                    <div className="mt-3">
                      <ReviewInChatForm 
                        reviewRequestId={msg.review_request_id} 
                        onSubmit={() => fetchMessages()}
                      />
                    </div>
                  )}
                  
                  <div className={`flex items-center justify-end gap-1 mt-1 ${msg.sender_type === 'user' ? 'text-blue-200' : 'text-gray-500'}`}>
                    <span className="text-[10px]">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {msg.sender_type === 'user' && (
                      msg.read_at ? (
                        <CheckCheck className="w-3 h-3 text-blue-300" title={t('seen')} />
                      ) : (
                        <Check className="w-3 h-3" title={t('delivered')} />
                      )
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* Spacer to push bot options to bottom */}
          <div className="flex-grow"></div>
          
          {/* Bot Greeting & Options - ALWAYS at BOTTOM when showing */}
          {showBotOptions && !chatEnded && (
            <div className="space-y-3 pt-2 mt-auto">
              {/* Bot Greeting */}
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white rounded-bl-sm border border-blue-500/30">
                  <p className="text-sm">{t('botGreeting')}</p>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl px-4 py-2 bg-white/10 text-white rounded-bl-sm">
                  <p className="text-sm">{t('botHowCanIHelp')}</p>
                </div>
              </div>
              
              {/* Quick Options */}
              <div className="space-y-2 pl-4">
                <Button
                  onClick={() => handleBotOption('payment')}
                  variant="outline"
                  className="w-full justify-start border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10"
                >
                  {t('paymentHelp')}
                </Button>
                <Button
                  onClick={() => handleBotOption('referral')}
                  variant="outline"
                  className="w-full justify-start border-green-500/30 text-green-300 hover:bg-green-500/10"
                >
                  {t('referralHelp')}
                </Button>
                <Button
                  onClick={() => handleBotOption('support')}
                  variant="outline"
                  className="w-full justify-start border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                >
                  {t('talkToSupport')}
                </Button>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Image Preview */}
        {imagePreview && (
          <div className="px-4 py-2 border-t border-white/10">
            <div className="relative inline-block">
              <img src={imagePreview} alt="Preview" className="h-20 rounded-lg" />
              <button
                onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        )}

        {/* Input - ALWAYS show so user can write */}
        <div className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="text-gray-400 hover:text-white hover:bg-white/10"
              title={t('attachImage')}
            >
              <Paperclip className="w-5 h-5" />
            </Button>
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t('typeMessage')}
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            />
            <Button
              onClick={() => sendMessage()}
              disabled={sending || (!newMessage.trim() && !selectedImage)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Messenger Modal (for admin to chat with a specific user)
export const AdminMessengerModal = ({ isOpen, onClose, user }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [lastUserActivity, setLastUserActivity] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [chatEnded, setChatEnded] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async (playSound = false) => {
    if (!user?.id) return;

    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/messages/${user.id}?token=${token}`);
      const data = await response.json();
      if (data.success) {
        const newMessages = data.messages || [];
        
        if (playSound && newMessages.length > lastMessageCount) {
          const newUserMessages = newMessages.filter(
            (m, i) => i >= lastMessageCount && m.sender_type === 'user'
          );
          if (newUserMessages.length > 0) {
            playNotificationSound();
          }
        }
        
        setMessages(newMessages);
        setLastMessageCount(newMessages.length);
        
        // Track last user activity
        const lastUserMsg = [...newMessages].reverse().find(m => m.sender_type === 'user');
        if (lastUserMsg) {
          setLastUserActivity(new Date(lastUserMsg.timestamp));
        }
        
        // Check if chat ended - but only block if it's the LAST message
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg && lastMsg.chat_ended) {
          setChatEnded(true);
        } else {
          setChatEnded(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        toast.error('Only PNG, JPG, JPEG images allowed');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedImage) return;
    if (!user?.id) return;

    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setSending(true);
    try {
      let imageData = null;
      if (selectedImage) {
        imageData = imagePreview;
      }

      const response = await fetch(`${API}/admin/messages/send?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: user.id, 
          message: newMessage.trim(),
          image: imageData
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        setSelectedImage(null);
        setImagePreview(null);
        fetchMessages();
        toast.success('Message sent!');
      } else {
        toast.error(data.message || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const endChat = async () => {
    if (!user?.id) return;
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    try {
      const response = await fetch(`${API}/admin/messages/end-chat?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });
      const data = await response.json();
      if (data.success) {
        setChatEnded(true);
        toast.success('Chat ended');
        fetchMessages();
      }
    } catch (err) {
      toast.error('Failed to end chat');
    }
  };

  // Check if no response for 20 minutes
  const canEndChat = () => {
    if (!lastUserActivity) return false;
    const now = new Date();
    const diffMinutes = (now - lastUserActivity) / (1000 * 60);
    return diffMinutes >= 20;
  };

  useEffect(() => {
    if (isOpen && user?.id) {
      setChatEnded(false);
      fetchMessages();
      const interval = setInterval(() => fetchMessages(true), 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, user?.id]); // eslint-disable-line

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-md h-[85vh] max-h-[600px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-purple-600/20 to-pink-600/20">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-medium">{user?.name || 'User'}</h3>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEndChat() && !chatEnded && (
              <Button
                onClick={endChat}
                size="sm"
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 text-xs"
                title="No response for 20 minutes"
              >
                <Clock className="w-3 h-3 mr-1" />
                End Chat
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Chat Ended Notice */}
          {chatEnded && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-red-300 text-sm">Chat ended due to inactivity</span>
              </div>
            </div>
          )}

          {loading && messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No messages yet</p>
              <p className="text-sm">Send a message to start the conversation</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.sender_type === 'admin'
                      ? 'bg-purple-600 text-white rounded-br-sm'
                      : msg.is_support_request
                        ? 'bg-yellow-500/20 text-white rounded-bl-sm border border-yellow-500/30'
                        : 'bg-white/10 text-white rounded-bl-sm'
                  }`}
                >
                  {msg.is_support_request && (
                    <div className="text-xs text-yellow-300 mb-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Support Request
                    </div>
                  )}
                  {/* Image if present */}
                  {msg.image && (
                    <img 
                      src={msg.image} 
                      alt="Shared" 
                      className="max-w-full rounded-lg mb-2 cursor-pointer"
                      onClick={() => window.open(msg.image, '_blank')}
                    />
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  <div className={`flex items-center justify-end gap-1 mt-1 ${msg.sender_type === 'admin' ? 'text-purple-200' : 'text-gray-500'}`}>
                    <span className="text-[10px]">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {msg.sender_type === 'admin' && (
                      msg.read_at ? (
                        <CheckCheck className="w-3 h-3 text-purple-300" title="Seen" />
                      ) : (
                        <Check className="w-3 h-3" title="Delivered" />
                      )
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Image Preview */}
        {imagePreview && (
          <div className="px-4 py-2 border-t border-white/10">
            <div className="relative inline-block">
              <img src={imagePreview} alt="Preview" className="h-20 rounded-lg" />
              <button
                onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        )}

        {/* Input - ALWAYS show for admin */}
        <div className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="text-gray-400 hover:text-white hover:bg-white/10"
              title="Attach Image"
            >
              <Paperclip className="w-5 h-5" />
            </Button>
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            />
            <Button
              onClick={sendMessage}
              disabled={sending || (!newMessage.trim() && !selectedImage)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Messenger Button with notification badge (for user)
export const MessengerButton = ({ onClick, onNewMessage }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastCount, setLastCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const fetchUnreadCount = async () => {
    const token = localStorage.getItem('guestToken');
    if (!token) return;

    try {
      const response = await fetch(`${API}/user/messages/unread-count?token=${token}`);
      const data = await response.json();
      if (data.success) {
        const newCount = data.unread_count || 0;
        
        // Only auto-open if this is a NEW message (count increased)
        if (newCount > lastCount && lastCount >= 0) {
          playNotificationSound();
          toast.info('📩 New message from Admin!', { duration: 3000 });
          // Reset dismissed state on new message
          setDismissed(false);
          // Auto-open chat when new message arrives
          if (onNewMessage) {
            onNewMessage();
          }
        }
        
        setUnreadCount(newCount);
        setLastCount(newCount);
      }
    } catch (err) {
      // Ignore
    }
  };

  // When user clicks to open chat, reset dismissed
  const handleClick = () => {
    setDismissed(false);
    onClick();
  };

  useEffect(() => {
    // Initial fetch
    const doFetch = async () => {
      await fetchUnreadCount();
    };
    doFetch();
    const interval = setInterval(fetchUnreadCount, 5000); // Check every 5 seconds for faster response
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  return (
    <button
      onClick={handleClick}
      className={`relative p-2 rounded-full transition-colors ${
        unreadCount > 0 
          ? 'bg-blue-500/30 hover:bg-blue-500/40 animate-pulse' 
          : 'bg-white/10 hover:bg-white/20'
      }`}
      title="Messages"
    >
      <MessageSquare className={`w-5 h-5 ${unreadCount > 0 ? 'text-blue-300' : 'text-white'}`} />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-bounce">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
};

// Admin Chat Bubble for header (shows total unread from all users with dropdown)
export const AdminChatBubble = ({ onSelectUser, onNewMessage }) => {
  const [totalUnread, setTotalUnread] = useState(0);
  const [lastTotal, setLastTotal] = useState(0);
  const [usersWithUnread, setUsersWithUnread] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = React.useRef(null);

  const fetchUnreadCounts = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    try {
      const response = await fetch(`${API}/admin/messages/unread-counts?token=${token}`);
      const data = await response.json();
      if (data.success) {
        const newTotal = data.total_unread || 0;
        const newUsers = data.users_with_unread || [];
        
        if (newTotal > lastTotal && lastTotal >= 0) {
          playNotificationSound();
          toast.info('📩 New message from user!', { duration: 3000 });
          
          // Auto-open chat with the user who sent the message
          if (onNewMessage && newUsers.length > 0) {
            // Find the user with the most recent message
            const latestUser = newUsers.reduce((latest, user) => {
              if (!latest || new Date(user.latest_timestamp) > new Date(latest.latest_timestamp)) {
                return user;
              }
              return latest;
            }, null);
            
            if (latestUser) {
              onNewMessage(latestUser);
            }
          }
        }
        
        setTotalUnread(newTotal);
        setLastTotal(newTotal);
        setUsersWithUnread(newUsers);
      }
    } catch (err) {
      // Ignore
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Initial fetch
    const doFetch = async () => {
      await fetchUnreadCounts();
    };
    doFetch();
    const interval = setInterval(fetchUnreadCounts, 5000); // Check every 5 seconds for faster response
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const handleUserClick = (user) => {
    setShowDropdown(false);
    if (onSelectUser) {
      onSelectUser(user);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative p-2 rounded-full transition-colors ${
          totalUnread > 0 
            ? 'bg-blue-500/20 hover:bg-blue-500/30 animate-pulse' 
            : 'bg-white/10 hover:bg-white/20'
        }`}
        title={totalUnread > 0 ? `${totalUnread} unread messages` : 'Messages'}
      >
        <MessageSquare className={`w-5 h-5 ${totalUnread > 0 ? 'text-blue-400' : 'text-white'}`} />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-bounce">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-slate-800 border border-white/20 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
            <h3 className="text-white font-medium flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Messages
              {totalUnread > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {totalUnread} unread
                </span>
              )}
            </h3>
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {usersWithUnread.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No unread messages</p>
              </div>
            ) : (
              usersWithUnread.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserClick(user)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                >
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-white font-medium truncate">{user.name}</p>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                      {user.unread_count}
                    </span>
                    <span className="text-[10px] text-gray-500">
                      {formatTime(user.latest_timestamp)}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMessengerModal;
